import { storeId } from 'Models/serverConfiguration';
import { mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import type { FitmentSearchCallbackConfig } from 'Models/uiConfig/fitmentSearchConfig.types';
import defaultFitmentSearchConfig from 'Platforms/shopify/fitmentSearchConfig';
import { safeCallback } from 'Utils/safeCallback';
import { trimEndSlash } from 'Utils/string';
import t from 'Utils/translate';
import { addPlatformRoute } from 'Utils/url';

const {
  fitmentSearch: {
    autoSyncKey: configuredAutoSyncKey,
    baseFields = [],
    categorySelectionPageRedirect,
    categorySelectionPages,
    doNotSaveSelectedVehicle,
    doNotLoadSelectedVehicle = false,
    extraFields = [],
    isExtraFieldsAlwaysVisible = false,
    forceSpecifyDialog,
    forceVehicleSelection = false,
    forceVehicleSelectionInterval = 60, // one hour
    getFitmentSearchTitle = () => t('ymm_bar_vehicle_subtitle'),
    isAutoRedirectDisabled,
    isAutoVehicleSelectionDisabled,
    hideVehicleFromUrl = false,
    getVerifyFitmentOrderNotes: customGetVerifyFitmentOrderNotes,
    onVehicleDiscarded: customOnVehicleDiscarded,
    onVehicleSelected: customOnVehicleSelected,
    onVerifyFitmentInitResponseReceived: customOnVerifyFitmentInitResponseReceived,
    onVerifyFitmentResponseReceived: customOnVerifyFitmentResponseReceived,
    hideVerifyOnCartItems = false,
    vehicleDataExpires,
  } = {},
  Widgets,
} = mainConfig;

const fitmentFields = [...baseFields, ...extraFields];
const autoSyncKey = configuredAutoSyncKey ?? storeId;
const isolatedHeader = 'isolatedHeader';
const isolatedDialog = 'isolatedDialog';

const categorySelectionPageList = (
  Array.isArray(categorySelectionPages)
    ? categorySelectionPages
    : categorySelectionPages
      ? [categorySelectionPages]
      : []
).map((page) => ({ ...page, pathname: addPlatformRoute(page.pathname) }));
const { pathname: categorySelectionPageUrl, field: categorySelectionPageField } =
  categorySelectionPageList[0] ?? {};

const currentCategorySelectionPage =
  categorySelectionPageList.find(
    (page) => trimEndSlash(window.location.pathname) === trimEndSlash(page.pathname),
  ) ?? null;
const currentCategorySelectionPageField = currentCategorySelectionPage?.field ?? null;
const isOnCategorySelectionPage = !!currentCategorySelectionPageField;
const hasSearchResults = Widgets.some((w) => w.visible && w.hasSearchResults);

const isFacetTilesVehicleWidgetEnabled = Widgets.some(
  (w) => w.visible && w.type === 'FacetTiles' && !hasSearchResults && !w.isVehicleWidgetDisabled,
);

const isFitmentSearchWidgetsVisible =
  isFacetTilesVehicleWidgetEnabled ||
  Widgets.some((w) => {
    const [nameRoot] = w.name.split('_');
    return (
      nameRoot !== 'DialogVehicleWidget' &&
      w.visible &&
      (w.type === 'VehicleTabs' ||
        w.type === 'VehicleWidget' ||
        w.type === 'VehicleTiles' ||
        w.type === 'VerifyFitment')
    );
  });

function getWidgetIsolatedKey(name: string): typeof isolatedHeader | typeof isolatedDialog | null {
  const [nameRoot] = name.split('_');
  switch (nameRoot) {
    case 'DialogVehicleWidget':
    case 'GarageVehicleWidget':
      return isolatedDialog;
    case 'HomeVehicleWidget':
      return isolatedHeader;
    case 'HeaderVehicleWidget':
    case 'FacetTilesVehicleWidget':
      return !hasSearchResults ? isolatedHeader : null;
    default:
      return null;
  }
}

const needInitFitmentSearchRequest =
  isFacetTilesVehicleWidgetEnabled ||
  Widgets.map((w) => w.visible && getWidgetIsolatedKey(w.name)).includes(isolatedHeader);
const needInitFitmentTableRequest = Widgets.some((w) => w.visible && w.type === 'FitmentTable');
const needInitVerifyFitmentRequest = Widgets.some((w) => w.visible && w.type === 'VerifyFitment');

const defaults: FitmentSearchCallbackConfig = defaultFitmentSearchConfig || {
  getVerifyFitmentOrderNotes: null,
  onVehicleSelected: null,
  onVehicleDiscarded: null,
  onVerifyFitmentInitResponseReceived: null,
  onVerifyFitmentResponseReceived: null,
};

const onVehicleSelected = safeCallback((vehicle) => {
  defaults.onVehicleSelected?.(vehicle);
  customOnVehicleSelected?.(vehicle);
  window.Convermax.onVehicleSelected?.(vehicle);
}, 'on_vehicle_selected');

const onVehicleDiscarded = safeCallback(() => {
  defaults.onVehicleDiscarded?.();
  customOnVehicleDiscarded?.();
}, 'on_vehicle_discarded');

const onVerifyFitmentInitResponseReceived = safeCallback((response) => {
  defaults.onVerifyFitmentInitResponseReceived?.(response);
  customOnVerifyFitmentInitResponseReceived?.(response);
}, 'on_verify_fitment_init_response');

const onVerifyFitmentResponseReceived = safeCallback((response) => {
  defaults.onVerifyFitmentResponseReceived?.(response);
  customOnVerifyFitmentResponseReceived?.(response);
}, 'on_verify_fitment_response');

const getVerifyFitmentOrderNotes = (fits: string): string | undefined => {
  const vehicleString = window.Convermax.getVehicleString();
  const vehicle = window.Convermax.getVehicle();
  const fieldsToSpecify = extraFields.filter((field) => !(field in vehicle)).join(', ');

  return (
    customGetVerifyFitmentOrderNotes?.(fits, vehicleString, fieldsToSpecify) ??
    defaults.getVerifyFitmentOrderNotes?.(fits, vehicleString, fieldsToSpecify)
  );
};

const fitmentSearchConfig = {
  autoSyncKey,
  baseFields,
  categorySelectionPageField,
  categorySelectionPageUrl,
  currentCategorySelectionPageField,
  doNotSaveSelectedVehicle,
  doNotLoadSelectedVehicle,
  extraFields,
  fields: fitmentFields,
  fitmentSearchTitle: getFitmentSearchTitle() || t('ymm_bar_vehicle_subtitle'),
  forceSpecifyDialog,
  forceVehicleSelection,
  forceVehicleSelectionInterval,
  hideVehicleFromUrl,
  garageDetected: Widgets.some((w) => w.type === 'Garage' || w.type === 'VehicleList'),
  isCategorySelectionPageRedirectEnabled: categorySelectionPageRedirect,
  isExtraFieldsAlwaysVisible,
  isOnCategorySelectionPage,
  isAutoRedirectDisabled,
  isAutoVehicleSelectionDisabled,
  isFitmentSearchWidgetsVisible,
  isAnyVehicleWidgetIsolated: Widgets.some(
    (w) => w.type === 'VehicleWidget' && w.visible && w.isVehicleSelectionIsolated,
  ),
  isVehicleWidgetInPartialMode: Widgets.some(
    (w) => (w.type === 'VehicleWidget' && w.fields) || w.type === 'VehicleTabs',
  ),
  needInitFitmentSearchRequest,
  needInitFitmentTableRequest,
  needInitVerifyFitmentRequest,
  getVerifyFitmentOrderNotes,
  onVehicleDiscarded,
  onVehicleSelected,
  onVerifyFitmentInitResponseReceived,
  onVerifyFitmentResponseReceived,
  hideVerifyOnCartItems,
  vehicleDataExpires,
};

window.Convermax.templates!.config.fitmentSearchConfig = fitmentSearchConfig;
export default fitmentSearchConfig;
